/**
 * Rejilla de la agenda: intervalos de 30 minutos (ADR-007, ADR-008).
 *
 * Una reserva es **un instante de inicio mas una duracion**, nunca "un dia y una hora".
 * Por eso el cruce de medianoche no es un caso especial: una reserva de 90 minutos que
 * empieza a las 23:30 ocupa tres slots consecutivos y termina a la 01:00, y este modulo
 * no necesita saber que hubo un cambio de dia.
 *
 * El identificador de slot se deriva del instante **UTC**, no de la hora local. Es el
 * detalle que hace imposible el solapamiento (ADR-008) y tambien el mas facil de
 * equivocar: `techada__20260802T0230Z` es el slot de las 23:30 del 1 de agosto en
 * Montevideo.
 */
import { DURACIONES_MINUTOS, MINUTOS_POR_SLOT, } from '../constantes.js';
import { errorDominio } from '../tipos/errores.js';
import { exito, fallo } from '../tipos/resultado.js';
import { textoDeFechaLocal, textoDeHoraLocal } from './civil.js';
import { sumarMinutos, sumarMs } from './instante.js';
import { localDesdeInstante } from './zona.js';
/** La hora de reloj cae exactamente sobre la rejilla de 30 minutos. */
export function horaAlineada(hora, intervaloMinutos = MINUTOS_POR_SLOT) {
    return (Number.isInteger(intervaloMinutos) &&
        intervaloMinutos > 0 &&
        1_440 % intervaloMinutos === 0 &&
        (hora.hora * 60 + hora.minuto) % intervaloMinutos === 0);
}
/** La duracion es una de las ofrecidas. */
export function esDuracionValida(minutos) {
    return DURACIONES_MINUTOS.includes(minutos);
}
/** Cantidad de slots que ocupa una duracion. 60 → 2, 90 → 3. */
export function cantidadDeSlots(duracion, intervaloMinutos = MINUTOS_POR_SLOT) {
    return duracion / intervaloMinutos;
}
/**
 * El instante cae sobre la rejilla.
 *
 * Se comprueba en **hora local**, no en UTC: la rejilla es la del mostrador. Da lo
 * mismo mientras el desfase sea de horas enteras, pero dejo de darlo si alguna vez la
 * zona tuviera un desfase de media hora.
 */
export function inicioAlineado(instante, zona, intervaloMinutos = MINUTOS_POR_SLOT) {
    if (instante % 60_000 !== 0) {
        return false;
    }
    return horaAlineada(localDesdeInstante(instante, zona).hora, intervaloMinutos);
}
/**
 * Genera los instantes de inicio de cada slot que ocupa una reserva.
 *
 * Devuelve 2 instantes para 60 minutos y 3 para 90.
 */
export function generarSlots(inicioUtc, duracion, zona, intervaloMinutos = MINUTOS_POR_SLOT) {
    if (!esDuracionValida(duracion)) {
        return fallo(errorDominio('DURACION_INVALIDA', 'duracion'));
    }
    if (!Number.isInteger(intervaloMinutos) ||
        intervaloMinutos <= 0 ||
        duracion % intervaloMinutos !== 0) {
        return fallo(errorDominio('DURACION_INVALIDA', 'intervaloMinutos'));
    }
    if (!inicioAlineado(inicioUtc, zona, intervaloMinutos)) {
        return fallo(errorDominio('INICIO_NO_ALINEADO', 'inicioUtc'));
    }
    const slots = [];
    for (let indice = 0; indice < cantidadDeSlots(duracion, intervaloMinutos); indice += 1) {
        slots.push(sumarMinutos(inicioUtc, indice * intervaloMinutos));
    }
    return exito(slots);
}
/** Intervalo que ocupa una reserva. El fin es exclusivo. */
export function intervaloDeReserva(inicioUtc, duracion) {
    return { inicioUtc, finUtc: sumarMinutos(inicioUtc, duracion) };
}
/**
 * Identificador determinista de un slot, derivado del instante **UTC**.
 *
 * Firestore no tiene restricciones de unicidad: que el identificador sea deterministico
 * es lo que permite que la transaccion lea y cree el documento y garantice la exclusion
 * mutua (ADR-008).
 */
export function idSlot(canchaId, instanteUtc) {
    const fecha = new Date(instanteUtc);
    const anio = String(fecha.getUTCFullYear()).padStart(4, '0');
    const mes = String(fecha.getUTCMonth() + 1).padStart(2, '0');
    const dia = String(fecha.getUTCDate()).padStart(2, '0');
    const hora = String(fecha.getUTCHours()).padStart(2, '0');
    const minuto = String(fecha.getUTCMinutes()).padStart(2, '0');
    return `${canchaId}__${anio}${mes}${dia}T${hora}${minuto}Z`;
}
/** Identificadores de todos los slots que ocupa una reserva. */
export function idsSlotsDeReserva(canchaId, inicioUtc, duracion, zona, intervaloMinutos = MINUTOS_POR_SLOT) {
    const slots = generarSlots(inicioUtc, duracion, zona, intervaloMinutos);
    if (!slots.ok) {
        return slots;
    }
    return exito(slots.valor.map((instante) => idSlot(canchaId, instante)));
}
/**
 * Dos intervalos se solapan.
 *
 * Semiabiertos: si uno termina exactamente donde empieza el otro **no** se solapan, que
 * es lo que permite encadenar turnos consecutivos en la misma cancha.
 */
export function seSolapan(a, b) {
    return a.inicioUtc < b.finUtc && b.inicioUtc < a.finUtc;
}
/**
 * Calcula los campos locales que se guardan junto a la reserva.
 *
 * Firestore no sabe convertir zonas dentro de una consulta, asi que el dia local se
 * persiste ya calculado para poder agrupar la caja y los reportes por dia (ADR-006).
 */
export function denormalizarInicioLocal(inicioUtc, duracion, zona) {
    const inicio = localDesdeInstante(inicioUtc, zona);
    // El intervalo es semiabierto: el ultimo instante ocupado es `fin - 1 ms`. Comparar
    // contra `fin` marcaria como cruce de medianoche una reserva de 23:00 a 00:00, que
    // termina justo en el limite y no ocupa ningun slot del dia siguiente. Ese error
    // escribiria una entrada espuria en la proyeccion publica del dia siguiente.
    const ultimoInstante = sumarMs(sumarMinutos(inicioUtc, duracion), -1);
    const ultimo = localDesdeInstante(ultimoInstante, zona);
    return {
        inicioLocalFecha: textoDeFechaLocal(inicio.fecha),
        inicioLocalHora: textoDeHoraLocal(inicio.hora),
        cruzaMedianoche: !sonMismaFecha(inicio.fecha, ultimo.fecha),
    };
}
function sonMismaFecha(a, b) {
    return a.anio === b.anio && a.mes === b.mes && a.dia === b.dia;
}
/** La reserva termina en un dia local distinto del que empieza. */
export function cruzaMedianoche(inicioUtc, duracion, zona) {
    return denormalizarInicioLocal(inicioUtc, duracion, zona).cruzaMedianoche;
}
//# sourceMappingURL=slots.js.map